import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnInit } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { finalize, map, Observable, tap } from 'rxjs';
import { Store } from '@ngxs/store';

import { Button } from 'primeng/button';
import { DynamicDialogModule, DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';

import { Organization } from '@features/organizations/models/organization.model';
import { AlertComponent } from '@shared/alert/alert.component';
import { OrganizationsState } from '@stores/organizations';
import {
  AddOrUpdateOrganization,
  LoadUserOrganizations,
  SelectOrganization,
} from '@stores/organizations/organizations.actions';

import { FormComponent } from '../Form/Form.component';
import { AdminsComponent } from '../Admins/Admins.component';
import { UserState } from '@stores/user';

@Component({
  selector: 'tr-organization-home',
  standalone: true,
  imports: [CommonModule, AlertComponent, Button, RouterModule, DynamicDialogModule],
  providers: [DialogService],
  templateUrl: './Home.component.html',
  styleUrl: './Home.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HomeComponent implements OnInit {
  private cdr = inject(ChangeDetectorRef);
  private store = inject(Store);
  private router = inject(Router);
  private dialogService = inject(DialogService);

  currentUser: Parse.User | null = this.store.selectSnapshot(UserState.getCurrentUser);

  currentOrg$: Observable<Organization | null | undefined> = this.store.select(OrganizationsState.getCurrentOrganization);
  isLoading$: Observable<boolean> = this.store.select(OrganizationsState.isLoading);
  organizations$: Observable<Organization[]> = this.store.select(OrganizationsState.getOrganizations);

  ownedOrganizations$: Observable<Organization[]> = this.organizations$.pipe(
    map((orgs) =>
      orgs.filter((org) => {
        return org.owner!.id === this.currentUser!.id;
      })
    )
  );

  adminOrganizations$: Observable<Organization[]> = this.organizations$.pipe(
    map((orgs) => orgs.filter((org) => org.owner!.id !== this.currentUser!.id))
  );

  orgFormRef: DynamicDialogRef | undefined;
  adminRef: DynamicDialogRef | undefined;

  loading = true;

  ngOnInit() {}

  ngOnDestroy(): void {
    if (this.orgFormRef) this.orgFormRef.close();
    if (this.adminRef) this.adminRef.close();
  }

  selectOrg(id: string) {
    this.store.dispatch(new SelectOrganization(id));
    this.router.navigate(['/members']);
  }

  openPublicPage(slug: string) {
    const url = '/' + slug;
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  refreshOrgs() {
    this.loading = true;
    this.store
      .dispatch(new LoadUserOrganizations(true))
      .pipe(
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe({});
  }

  showOrganizationForm(org: Organization | null) {
    this.orgFormRef = this.dialogService.open(FormComponent, {
      header: `${org ? 'Edit' : 'Create'} Organization`,
      width: '40vw',
      modal: true,
      closeOnEscape: true,
      closable: true,
      breakpoints: {
        '960px': '75vw',
        '640px': '95vw',
      },
      data: {
        Organization: org,
      },
    });

    this.orgFormRef.onClose.subscribe((result: Organization | { deleted: true } | null | undefined) => {
      if (result && 'deleted' in result) {
        this.router.navigate(['/organizations']);
        this.cdr.detectChanges();
      } else if (result) {
        this.store.dispatch(new AddOrUpdateOrganization(result as Organization));
        this.cdr.detectChanges();
      }
    });
  }

  showAdminsForm(org: Organization) {
    this.adminRef = this.dialogService.open(AdminsComponent, {
      header: `Manage Admins for ${org.name}`,
      width: '50vw',
      modal: true,
      closeOnEscape: true,
      keepInViewport: true,
      closable: true,
      breakpoints: {
        '960px': '75vw',
        '640px': '95vw',
      },
      data: {
        Organization: org,
      },
    });
  }
}
